import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createAxiosInstance } from "../axiosSettings";

// Initial state
const initialState = {
    subCategory: [],
    subCategories: [],
    subServices: [],
    loading: false,
    error: null,
};

// Helper: transform service object
const transformService = (service) => {
    const id = service._id || service.id;
    const name = service.name || '';
    const baseURL = (process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api').replace('/api', '');

    let imageUrl = null;
    const imageFields = [
        service.image,
        service.imageUrl,
        service.imagePath,
        service.photo,
        service.photoUrl,
        service.picture,
        service.pictureUrl
    ];

    for (const field of imageFields) {
        if (field !== null && field !== undefined) {
            if (typeof field === 'object' && field !== null) {
                imageUrl = field.url || field.path || field.src || field.image || field.imageUrl || null;
                if (imageUrl) break;
            } else if (typeof field === 'string' && field.trim() !== '') {
                imageUrl = field;
                break;
            }
        }
    }

    if (imageUrl && typeof imageUrl === 'string') {
        const trimmed = imageUrl.trim();
        if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
            imageUrl = null;
        } else if (trimmed.startsWith('http') || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
            const urlObj = new URL(trimmed);
            const pathname = urlObj.pathname;
            if (
                pathname.startsWith('/') &&
                !pathname.startsWith('/uploads/') &&
                !pathname.startsWith('/images/') &&
                !pathname.startsWith('/public/') &&
                pathname !== '/' &&
                pathname.split('/').length === 2
            ) {
                urlObj.pathname = `/uploads${pathname}`;
                imageUrl = urlObj.toString();
            } else {
                imageUrl = trimmed;
            }
        } else {
            let imagePath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
            if (!imagePath.startsWith('/uploads/') && !imagePath.startsWith('/images/') && !imagePath.startsWith('/public/')) {
                imagePath = `/uploads${imagePath}`;
            }
            imageUrl = `${baseURL}${imagePath}`;
        }
    } else {
        imageUrl = null;
    }

    const status = service.status ?? service.isActive;
    const isActive =
        typeof status === 'number'
            ? status === 1
            : typeof status === 'string'
                ? status.toLowerCase() === 'active'
                : Boolean(status);

    return { ...service, id, name, image: imageUrl, isActive };
};

export const createSubCategory = createAsyncThunk('categories/create', async (payload, { rejectWithValue }) => {
    try {
        const formData = new FormData();
        const subCategoryName = payload.name.trim();

        if (!subCategoryName) {
            return rejectWithValue('SubCategory name cannot be empty');
        }

        formData.append('name', subCategoryName);
        formData.append('categoryId', payload?.categoryId);
        formData.append('type', payload?.type);
        formData.append('status', payload.status ? 1 : 0);

        if (payload.image) {
            if (payload.image instanceof File || payload.image instanceof Blob) {
                formData.append('image', payload.image);
            } else if (typeof payload.image === 'string' && payload.image.trim() !== '') {
                formData.append('image', payload.image);
            }
        }
        const axiosInstance = await createAxiosInstance();

        const response = await axiosInstance.post('/admin/categories/sub-category', formData);
        let serviceData = response.data;
        if (serviceData && (serviceData.data || serviceData.service)) {
            serviceData = serviceData.data || serviceData.service;
        }

        return transformService(serviceData);
    } catch (err) {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create service';
        return rejectWithValue(errorMessage);
    }
});

export const fetchSubCategories = createAsyncThunk('categories/subCategories/fetchAll', async (payload, { rejectWithValue }) => {
    try {
        const axiosInstance = await createAxiosInstance();
        const { data } = await axiosInstance.get('/admin/categories/sub-categories' + '/' + payload);
        return Array.isArray(data?.data) ? data?.data.map(transformService) : [];
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

export const fetchSubServices = createAsyncThunk('categories/subServices/fetchAll', async (payload, { rejectWithValue }) => {
    try {
        const axiosInstance = await createAxiosInstance();
        const { data } = await axiosInstance.get('/admin/categories/sub-services' + '/' + payload);
        return Array.isArray(data?.data) ? data?.data.map(transformService) : [];
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

export const deleteSubCategory = createAsyncThunk('categories/delete', async (id, { rejectWithValue }) => {
    try {
        const axiosInstance = await createAxiosInstance();
        await axiosInstance.delete(`/admin/categories/${id}/sub-category`);
        return id;
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

export const updateSubCategory = createAsyncThunk('categories/update', async ({ id, data }, { rejectWithValue }) => {
    try {
        const formData = new FormData();
        if (data.name) formData.append('name', data.name);
        if (data.isActive !== undefined) formData.append('status', data.isActive ? 1 : 0);
        if (data.image) formData.append('image', data.image);

        const serviceId = id || data._id || data.id;
        const axiosInstance = await createAxiosInstance();
        const response = await axiosInstance.post(`/admin/categories/update/${serviceId}/sub-category`, formData);
        return response.data;
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

export const toggleSubCategoryStatus = createAsyncThunk('categories/toggleStatus/subCategory', async ({ id, isActive }, { rejectWithValue }) => {
    try {
        const axiosInstance = await createAxiosInstance();
        const { data } = await axiosInstance.post(
            `/admin/categories/update/${id}/sub-category`,
            { status: isActive ? 1 : 0 },
            { headers: { 'Content-Type': 'application/json' } }
        );
        return data;
    } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
    }
});

const subCategorySlice = createSlice({
    name: 'subCategory',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(createSubCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(createSubCategory.fulfilled, (state, action) => {
                state.loading = false;
                // state.subCategory = action.payload;
                const newService = action.payload;
                if (newService && !state.subCategories.some((s) => s.id === newService.id || s._id === newService.id)) {
                    state.subCategories.push(newService);
                }
            })
            .addCase(createSubCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchSubCategories.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSubCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.subCategories = action.payload;
            })
            .addCase(fetchSubCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchSubServices.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSubServices.fulfilled, (state, action) => {
                state.loading = false;
                state.subServices = action.payload;
            })
            .addCase(fetchSubServices.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(deleteSubCategory.fulfilled, (state, action) => {
                state.subCategories = state.subCategories.filter((s) => s.id !== action.payload && s._id !== action.payload);
            })
            .addCase(deleteSubCategory.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(updateSubCategory.fulfilled, (state, action) => {
                const updated = transformService(action.payload);
                const index = state.subCategories.findIndex((s) => s.id === updated.id);
                if (index >= 0) state.subCategories[index] = updated;
                else state.subCategories.push(updated);
            })
            .addCase(updateSubCategory.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(toggleSubCategoryStatus.fulfilled, (state, action) => {
                const updated = transformService(action.payload);
                const index = state.subCategories.findIndex((s) => s.id === updated.id);
                if (index >= 0) state.subCategories[index] = updated;
            })
            .addCase(toggleSubCategoryStatus.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

// Exports
export const { clearError, resetState } = subCategorySlice.actions;
export const selectSubCategories = (state) => state?.services?.services;
export const selectSubCategoriesLoading = (state) => state?.services?.loading;
export const selectSubCategoriesError = (state) => state?.services?.error;
export const selectServiceById = (id) => (state) => state?.services?.services.find((s) => s.id === id);

export default subCategorySlice.reducer;
